/**
 * Name + voice, collected on the landing beside the graphics selector. Same
 * shape as `buildGraphics`: it owns its own persistence and exposes a getter,
 * so nothing upstream has to hold the value.
 *
 * The name is sanitized on read, not on keystroke — typing a space mid-name
 * must not fight the caret.
 */
import {
  DEFAULT_NAME,
  MAX_NAME,
  readIdentity,
  sanitizeName,
  writeIdentity,
  type PlayerIdentity,
  type PlayerVoice,
} from '../../game/Identity';

const VOICE_LABELS: ReadonlyArray<readonly [PlayerVoice, string]> = [
  ['male', 'Male'],
  ['female', 'Female'],
  ['neutral', 'Prefer not to say'],
];

export interface IdentityControl {
  readonly el: HTMLElement;
  identity(): PlayerIdentity;
}

export function buildIdentity(): IdentityControl {
  const stored = readIdentity();
  let voice = stored.voice;
  // Empty rather than the default, so the field reads as a prompt and the
  // placeholder shows what happens if it is left alone.
  let typed = stored.name === DEFAULT_NAME ? '' : stored.name;

  const wrap = el('div', 'landing-identity');

  const nameLabel = el('label', 'landing-graphics-label');
  nameLabel.textContent = 'NAME';
  nameLabel.htmlFor = 'ls-name';

  const input = el('input', 'landing-name') as HTMLInputElement;
  input.id = 'ls-name';
  input.type = 'text';
  input.value = typed;
  input.maxLength = MAX_NAME;
  input.placeholder = DEFAULT_NAME;
  input.autocomplete = 'off';
  input.spellcheck = false;
  input.setAttribute('aria-label', 'Your name');
  input.addEventListener('click', (e) => e.stopPropagation());
  input.addEventListener('input', () => {
    typed = input.value;
    persist();
  });

  const voiceLabel = el('div', 'landing-graphics-label');
  voiceLabel.textContent = 'VOICE';
  const row = el('div', 'landing-seg');
  row.setAttribute('role', 'radiogroup');
  row.setAttribute('aria-label', 'Voice');

  const buttons = new Map<PlayerVoice, HTMLButtonElement>();
  for (const [key, text] of VOICE_LABELS) {
    const b = el('button', 'landing-seg-btn') as HTMLButtonElement;
    b.type = 'button';
    b.setAttribute('role', 'radio');
    b.textContent = text;
    const selected = key === voice;
    b.setAttribute('aria-checked', String(selected));
    if (selected) b.classList.add('on');
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      if (key === voice) return;
      voice = key;
      for (const [k, btn] of buttons) {
        const on = k === voice;
        btn.classList.toggle('on', on);
        btn.setAttribute('aria-checked', String(on));
      }
      persist();
    });
    buttons.set(key, b);
    row.appendChild(b);
  }

  function current(): PlayerIdentity {
    return { name: sanitizeName(typed), voice };
  }
  function persist(): void {
    writeIdentity(current());
  }

  wrap.append(nameLabel, input, voiceLabel, row);
  return { el: wrap, identity: current };
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  n.className = className;
  return n;
}
